import { useLayoutEffect, useMemo, useState } from "react";

import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "../../ui/sheet";
import { useSessionAuditEvents } from "../../../hooks/audit/useAuditEvents";
import { useMemberEmailLookup } from "../../../hooks/audit/useMemberEmailLookup";
import { cn } from "../../../lib/utils";
import { AuditEventDetailPane } from "./AuditEventDetailPane";
import { AuditEventRail } from "./AuditEventRail";
import { AuditLogFilterChips } from "./AuditLogFilterChips";
import {
  categoryLabel,
  eventTypesForCategory,
  type AuditCategoryId,
} from "./eventCategories";

interface AuditLogSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  controlSessionId: string;
  sessionName: string;
  /** Resolves an attribute-test-column id to its name, so a run's target
   * columns read as attributes rather than uuids. */
  columnNameById?: (columnId: string) => string | undefined;
}

/**
 * Session-scoped Audit Log drawer for the work-paper view.
 *
 * Opens as a single-column timeline; selecting an event expands the drawer
 * and reveals the detail pane beside the rail, and the pane's own close
 * control collapses it back without dismissing the drawer. Controlled by the
 * caller so the trigger and the sheet can live in different parts of the
 * workpaper header.
 *
 * The feed hook resets its accumulated events + cursor and refetches page 1
 * whenever `enabled` or the filter identity flips (see `useAuditEventFeed` in
 * `useAuditEvents.ts`), so gating on `open` alone is sufficient to show fresh
 * events on every reopen — no extra `refresh()` call is needed here, which
 * would otherwise double-fetch page 1 on open.
 */
export function AuditLogSheet({
  open,
  onOpenChange,
  controlSessionId,
  sessionName,
  columnNameById,
}: AuditLogSheetProps) {
  const [category, setCategory] = useState<AuditCategoryId | null>(null);
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
  // Whether the detail pane is open, tracked separately from which event is
  // selected: switching category should keep the pane open and re-anchor it,
  // not close it just because the selected event isn't in the new results.
  const [detailOpen, setDetailOpen] = useState(false);
  const memberEmailById = useMemberEmailLookup(open);

  // Every open starts fresh: the All tab, nothing selected, single column.
  // Layout effect so the reset lands before paint — no flash of the previous
  // visit's category or expanded pane.
  useLayoutEffect(() => {
    if (open) {
      setCategory(null);
      setSelectedEventId(null);
      setDetailOpen(false);
    }
  }, [open]);

  const filters = useMemo(
    () => ({
      eventTypes: category ? eventTypesForCategory(category) : undefined,
    }),
    [category],
  );

  const { events, loading, hasMore, loadMore } = useSessionAuditEvents(
    controlSessionId,
    filters,
    { enabled: open },
  );

  const selectedEvent =
    events.find((event) => event.id === selectedEventId) ?? null;

  // Re-anchor the open pane to the newest row whenever the current selection
  // isn't in the feed — the case after a category switch. Guarded on `loading`
  // so the selection survives the in-flight refetch rather than jumping to a
  // stale first row. An empty category leaves nothing to select, which collapses
  // the drawer to a single column until a non-empty one is chosen again.
  // Layout effect: it must land before paint, or the frame between "results
  // arrived" and "new selection set" renders with the pane collapsed and the
  // drawer visibly snaps narrow then wide again on every category switch.
  useLayoutEffect(() => {
    if (!detailOpen || loading || selectedEvent !== null) {
      return;
    }
    setSelectedEventId(events.length > 0 ? events[0].id : null);
  }, [detailOpen, loading, selectedEvent, events]);

  // Keep the drawer wide across the refetch that follows a category switch, so
  // it doesn't collapse and re-expand while the new page is in flight.
  const paneExpanded = detailOpen && (selectedEvent !== null || loading);

  const emptyLabel = category
    ? `No ${categoryLabel(category).toLowerCase()} events recorded for this test`
    : "No activity recorded yet";

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className={cn(
          "flex w-full flex-col gap-0 p-0 transition-[max-width] duration-200 ease-out",
          paneExpanded ? "sm:max-w-[880px]" : "sm:max-w-[480px]",
        )}
      >
        <SheetHeader className="shrink-0 space-y-3 border-b border-stone-200 px-6 py-4 pr-10">
          <div className="space-y-0.5">
            <SheetTitle className="text-base">Audit Log</SheetTitle>
            <SheetDescription className="text-xs">
              {sessionName}
            </SheetDescription>
          </div>
          <AuditLogFilterChips value={category} onChange={setCategory} />
        </SheetHeader>
        <div className="flex min-h-0 flex-1">
          <div
            className={cn(
              "overflow-y-auto",
              paneExpanded
                ? "w-[400px] shrink-0 border-r border-stone-200"
                : "flex-1",
            )}
          >
            <AuditEventRail
              events={events}
              loading={loading}
              hasMore={hasMore}
              loadMore={loadMore}
              selectedEventId={selectedEventId}
              onSelect={(event) => {
                setSelectedEventId(event.id);
                setDetailOpen(true);
              }}
              emptyLabel={emptyLabel}
              onClearFilter={category ? () => setCategory(null) : undefined}
              memberEmailById={memberEmailById}
              columnNameById={columnNameById}
            />
          </div>
          {paneExpanded && selectedEvent && (
            <div className="min-w-0 flex-1">
              <AuditEventDetailPane
                event={selectedEvent}
                onClose={() => {
                  setDetailOpen(false);
                  setSelectedEventId(null);
                }}
                memberEmailById={memberEmailById}
                columnNameById={columnNameById}
                scopedToTest
              />
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
